import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { DEFAULT_CUSTOMER, DEFAULT_CUSTOMER_CARD, money, newDriverCard, rateManifest } from './rateEngine';
import { downloadRateCardTemplate, parseRateCardFile } from './rateCardFile';

/** Numeric input that keeps the raw text while typing and commits a number (or null when blank) on blur. */
const NumField = ({ id, value, onCommit, allowBlank = false, className = '', step = '0.01' }) => {
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  const commit = () => {
    if (text === '' && allowBlank) return onCommit(null);
    const n = parseFloat(text);
    if (Number.isFinite(n)) onCommit(n);
    else setText(value ?? '');
  };
  return (
    <Input
      id={id}
      type="number"
      step={step}
      value={text}
      placeholder={allowBlank ? 'N/A' : ''}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      className={`h-8 text-right ${className}`}
    />
  );
};

const normFsa = (s) => s.replace(/\s/g, '').toUpperCase().slice(0, 3);
const isFsa = (s) => /^[A-Z]\d[A-Z]$/.test(s);
const sortedZones = (zones) => Object.keys(zones || {}).sort((a, b) => Number(a) - Number(b));

/** Editable list of weight or dims tiers. `kind` = 'weight' | 'dims'. */
const TierEditor = ({ idPrefix, tiers, kind, onChange, allowMode = true }) => {
  const minKey = kind === 'weight' ? 'minLb' : 'minIn';
  const update = (i, patch) => onChange(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 text-[11px] uppercase text-muted-foreground">
        <span className="col-span-4">Label</span>
        <span className="col-span-2 text-right">{kind === 'weight' ? 'Min lb' : 'Min in'}</span>
        <span className="col-span-3">{allowMode ? 'Mode' : ''}</span>
        <span className="col-span-2 text-right">$ / pc</span>
      </div>
      {tiers.map((t, i) => (
        <div key={`${t.code}-${i}`} className="grid grid-cols-12 gap-2 items-center">
          <Input
            id={`${idPrefix}-label-${i}`}
            value={t.label}
            onChange={(e) => update(i, { label: e.target.value })}
            onBlur={(e) => !t.code && update(i, { code: e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, '_') })}
            className="col-span-4 h-8"
          />
          <NumField id={`${idPrefix}-min-${i}`} value={t[minKey]} onCommit={(v) => update(i, { [minKey]: v })} className="col-span-2" step="0.01" />
          {allowMode ? (
            <select
              id={`${idPrefix}-mode-${i}`}
              value={t.mode}
              onChange={(e) => update(i, { mode: e.target.value })}
              className="col-span-3 bg-background border border-border rounded text-sm h-8 px-1"
            >
              <option value="replace">Replaces zone rate</option>
              <option value="add">Added to zone rate</option>
            </select>
          ) : (
            <span className="col-span-3 text-xs text-muted-foreground">Added</span>
          )}
          <NumField id={`${idPrefix}-amount-${i}`} value={t.amount} onCommit={(v) => update(i, { amount: v })} className="col-span-2" />
          <button className="col-span-1 text-muted-foreground hover:text-red-500" onClick={() => onChange(tiers.filter((_, j) => j !== i))} title="Remove">
            <i className="fas fa-times"></i>
          </button>
        </div>
      ))}
      <Button
        id={`${idPrefix}-add`}
        size="sm"
        variant="outline"
        onClick={() => onChange([...tiers, kind === 'weight' ? { code: '', label: '', minLb: 0, mode: allowMode ? 'replace' : 'add', amount: 0 } : { code: '', label: '', minIn: 0, amount: 0 }])}
      >
        <i className="fas fa-plus mr-2"></i>Add {kind === 'weight' ? 'weight' : 'dims'} tier
      </Button>
    </div>
  );
};

/** Upload + template buttons shared by both card types. */
const FileActions = ({ idPrefix, kind, card, fileName, onImported }) => {
  const ref = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { patch, summary, warnings } = await parseRateCardFile(file, kind);
      onImported(patch);
      toast.success(`Imported ${summary.join(', ')} — review, then Save`);
      if (warnings.length) toast.warning(`${warnings.length} row(s) skipped: ${warnings.slice(0, 3).join('; ')}${warnings.length > 3 ? '…' : ''}`);
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div className="flex gap-2">
      <input ref={ref} type="file" accept=".xlsx,.xls,.csv" className="hidden" id={`${idPrefix}-file`} onChange={onFile} />
      <Button id={`${idPrefix}-upload`} size="sm" variant="outline" onClick={() => ref.current?.click()}>
        <i className="fas fa-file-upload mr-2"></i>Upload rate card
      </Button>
      <Button id={`${idPrefix}-template`} size="sm" variant="ghost" onClick={() => downloadRateCardTemplate(kind, card, fileName)}>
        <i className="fas fa-file-excel mr-2"></i>Download as Excel
      </Button>
    </div>
  );
};

const SaveBar = ({ idPrefix, dirty, saving, onSave, onDiscard, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="flex flex-wrap items-center gap-2">{children}</div>
    <div className="flex items-center gap-2">
      {dirty && <span className="text-xs text-amber-500">Unsaved changes</span>}
      <Button id={`${idPrefix}-discard`} size="sm" variant="outline" disabled={!dirty || saving} onClick={onDiscard}>
        Discard
      </Button>
      <Button id={`${idPrefix}-save`} size="sm" disabled={!dirty || saving} onClick={onSave} className="bg-emerald-600 hover:bg-emerald-700 text-white">
        <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'} mr-2`}></i>Save
      </Button>
    </div>
  </div>
);

const useDraft = (source) => {
  const [draft, setDraft] = useState(source);
  useEffect(() => setDraft(source), [source]);
  return [draft, setDraft, JSON.stringify(draft) !== JSON.stringify(source)];
};

const useSaving = () => {
  const [saving, setSaving] = useState(false);
  const run = async (fn) => {
    setSaving(true);
    try {
      await fn();
    } finally {
      setSaving(false);
    }
  };
  return [saving, run];
};

// ---------------------------------------------------------------- Customer cards

const CustomerCardEditor = ({ cards, manifests, onSave }) => {
  const names = Object.keys(cards.customers || {}).sort();
  const [customer, setCustomer] = useState(names[0] || DEFAULT_CUSTOMER);
  const [newCustomer, setNewCustomer] = useState('');
  const source = useMemo(
    () => cards.customers?.[customer] || { ...DEFAULT_CUSTOMER_CARD, customer, fsaZone: {}, zones: {} },
    [cards.customers, customer]
  );
  const [draft, setDraft, dirty] = useDraft(source);
  const [saving, run] = useSaving();
  const [fsaSearch, setFsaSearch] = useState('');
  const [newFsa, setNewFsa] = useState('');
  const zones = sortedZones(draft.zones);
  const [newZone, setNewZone] = useState('1');

  const setZone = (z, patch) => setDraft((d) => ({ ...d, zones: { ...d.zones, [z]: { ...d.zones[z], ...patch } } }));
  const mapFsa = (fsa, zone) => setDraft((d) => ({ ...d, fsaZone: { ...d.fsaZone, [fsa]: Number(zone) } }));
  const unmapFsa = (fsa) =>
    setDraft((d) => {
      const { [fsa]: _, ...rest } = d.fsaZone;
      return { ...d, fsaZone: rest };
    });
  const setTiers = (key, tiers) => setDraft((d) => ({ ...d, accessorials: { ...d.accessorials, [key]: tiers } }));

  const fsaCountByZone = useMemo(() => {
    const counts = {};
    Object.values(draft.fsaZone || {}).forEach((z) => (counts[z] = (counts[z] || 0) + 1));
    return counts;
  }, [draft.fsaZone]);

  // FSAs on this customer's unposted manifests that the draft can't price yet.
  const unmapped = useMemo(() => {
    const counts = {};
    const preview = { ...cards, customers: { ...cards.customers, [customer]: draft } };
    manifests
      .filter((m) => m.status !== 'posted' && (m.customer || DEFAULT_CUSTOMER) === customer)
      .forEach((m) =>
        rateManifest(m, preview).pieces.forEach((p) => {
          if (p.fsa && p.flags.includes('UNMAPPED_FSA')) counts[p.fsa] = (counts[p.fsa] || 0) + 1;
        })
      );
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [manifests, cards, customer, draft]);

  const fsaRows = useMemo(() => {
    const q = fsaSearch.trim().toUpperCase();
    return Object.entries(draft.fsaZone || {})
      .filter(([fsa, z]) => !q || fsa.startsWith(q) || String(z) === q)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [draft.fsaZone, fsaSearch]);

  const addCustomer = () => {
    const name = newCustomer.trim();
    if (!name || names.includes(name)) return;
    setCustomer(name);
    setNewCustomer('');
  };

  return (
    <div className="space-y-6">
      <SaveBar idPrefix="customer-card" dirty={dirty} saving={saving} onSave={() => run(() => onSave(draft))} onDiscard={() => setDraft(source)}>
        <select
          id="customer-card-select"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
          className="bg-background border border-border rounded text-sm h-8 px-2 font-semibold"
        >
          {[...new Set([...names, customer])].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <Input id="customer-card-new" value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} placeholder="New customer…" className="h-8 w-[160px]" />
        <Button id="customer-card-add" size="sm" variant="ghost" disabled={!newCustomer.trim()} onClick={addCustomer}>
          <i className="fas fa-plus"></i>
        </Button>
        <FileActions idPrefix="customer-card" kind="customer" card={draft} fileName={`rate_card_${customer}.xlsx`} onImported={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
      </SaveBar>

      {unmapped.length > 0 && (
        <Card className="bg-card border-amber-500/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-500">
              <i className="fas fa-map-marker-alt mr-2"></i>
              {unmapped.length} FSA(s) on open {customer} manifests aren't on this card
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {unmapped.map(([fsa, count]) => (
              <div key={fsa} className="flex items-center gap-2 border border-border rounded-md px-2 py-1">
                <span className="font-mono font-semibold">{fsa}</span>
                <span className="text-xs text-muted-foreground">{count} pcs</span>
                <select
                  id={`rate-card-map-${fsa}`}
                  className="bg-background border border-border rounded text-sm h-7 px-1"
                  defaultValue=""
                  onChange={(e) => e.target.value && mapFsa(fsa, e.target.value)}
                >
                  <option value="" disabled>
                    Zone…
                  </option>
                  {zones.map((z) => (
                    <option key={z} value={z}>
                      Zone {z}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-12 xl:col-span-5 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Zone rates ($ / piece)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Zone</th>
                  <th className="text-right p-3">Customer zone #</th>
                  <th className="text-right p-3">Rate</th>
                  <th className="text-right p-3">FSAs</th>
                </tr>
              </thead>
              <tbody>
                {zones.map((z) => (
                  <tr key={z} className="border-t border-border">
                    <td className="p-3 font-semibold">
                      Zone {z} {draft.zones[z]?.rate == null && <Badge variant="outline" className="ml-2 text-[10px]">N/A</Badge>}
                    </td>
                    <td className="p-2">
                      <NumField id={`rate-amazon-zone-${z}`} value={draft.zones[z]?.amazonZone} step="1" allowBlank onCommit={(v) => setZone(z, { amazonZone: v })} className="w-20 ml-auto" />
                    </td>
                    <td className="p-2">
                      <NumField id={`rate-customer-${z}`} value={draft.zones[z]?.rate} allowBlank onCommit={(v) => setZone(z, { rate: v })} className="w-24 ml-auto" />
                    </td>
                    <td className="p-3 text-right text-muted-foreground">{fsaCountByZone[z] || 0}</td>
                  </tr>
                ))}
                {zones.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-muted-foreground">Upload the customer's rate card to load zones.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="col-span-12 xl:col-span-7 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Accessorial charges</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <p className="text-sm font-medium mb-2">Weight (highest matching tier applies)</p>
              <TierEditor idPrefix="customer-weight" kind="weight" tiers={draft.accessorials?.weightTiers || []} onChange={(t) => setTiers('weightTiers', t)} />
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Dimensions — longest side</p>
              <TierEditor idPrefix="customer-dims" kind="dims" allowMode={false} tiers={draft.accessorials?.dimsTiers || []} onChange={(t) => setTiers('dimsTiers', t)} />
              <p className="text-xs text-muted-foreground mt-2">The manifest only gives volume, so the longest side is estimated as ∛volume (a lower bound).</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">FSA → Zone ({Object.keys(draft.fsaZone || {}).length})</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Input id="rate-fsa-search" value={fsaSearch} onChange={(e) => setFsaSearch(e.target.value)} placeholder="Search FSA or zone…" className="w-[180px] h-8" />
              <Input id="rate-fsa-new" value={newFsa} onChange={(e) => setNewFsa(e.target.value)} placeholder="New FSA e.g. J3L" className="w-[140px] h-8 font-mono uppercase" />
              <select id="rate-fsa-new-zone" value={newZone} onChange={(e) => setNewZone(e.target.value)} className="bg-background border border-border rounded text-sm h-8 px-2">
                {zones.map((z) => (
                  <option key={z} value={z}>
                    Zone {z}
                  </option>
                ))}
              </select>
              <Button
                id="rate-fsa-add"
                size="sm"
                variant="outline"
                disabled={!isFsa(normFsa(newFsa)) || !zones.length}
                onClick={() => {
                  mapFsa(normFsa(newFsa), newZone);
                  setNewFsa('');
                }}
              >
                <i className="fas fa-plus mr-2"></i>Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2 max-h-[360px] overflow-y-auto">
            {fsaRows.map(([fsa, z]) => (
              <div key={fsa} className="flex items-center justify-between border border-border rounded-md px-2 py-1 text-sm group">
                <span className="font-mono">{fsa}</span>
                <span className="text-muted-foreground">Z{z}</span>
                <button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500" onClick={() => unmapFsa(fsa)} title="Remove">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------- Driver cards

const driverCardStatus = (card, zones) => {
  if (!card) return { label: 'No card', cls: 'border-red-500/40 text-red-500' };
  const missing = zones.filter((z) => z.rate != null && card.zones?.[z.zone] == null).length;
  return missing ? { label: `${missing} zone(s) missing`, cls: 'border-amber-500/40 text-amber-500' } : { label: 'Complete', cls: 'border-emerald-500/40 text-emerald-500' };
};

const DriverCardEditor = ({ cards, drivers, onSave, onDelete }) => {
  const customerCard = cards.customers?.[DEFAULT_CUSTOMER] || Object.values(cards.customers || {})[0] || DEFAULT_CUSTOMER_CARD;
  const serviced = sortedZones(customerCard.zones).map((z) => ({ zone: z, rate: customerCard.zones[z]?.rate }));
  const [driverId, setDriverId] = useState(drivers[0]?.id);
  const driver = drivers.find((d) => d.id === driverId);
  const existing = driverId ? cards.drivers?.[driverId] : undefined;
  const [copyFrom, setCopyFrom] = useState('');
  const [draft, setDraft, dirty] = useDraft(existing || null);
  const [saving, run] = useSaving();

  useEffect(() => {
    if (!driverId && drivers[0]) setDriverId(drivers[0].id);
  }, [drivers, driverId]);

  const create = () => {
    const template = copyFrom && cards.drivers?.[copyFrom];
    setDraft(template ? { ...template, driverId: driver.id, driverName: driver.name } : newDriverCard(driver, customerCard));
  };

  const others = Object.values(cards.drivers || {}).filter((c) => c.driverId !== driverId);

  return (
    <div className="grid grid-cols-12 gap-6">
      <Card className="col-span-12 lg:col-span-4 bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Drivers ({drivers.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 max-h-[620px] overflow-y-auto">
          {drivers.map((d) => {
            const st = driverCardStatus(cards.drivers?.[d.id], serviced);
            return (
              <button
                key={d.id}
                id={`driver-card-item-${d.id}`}
                onClick={() => setDriverId(d.id)}
                className={`w-full flex items-center justify-between text-left p-2 rounded-md border ${d.id === driverId ? 'border-emerald-500 bg-emerald-500/10' : 'border-transparent hover:bg-muted/40'}`}
              >
                <span className="text-sm">
                  {d.name}
                  <span className="block text-[11px] text-muted-foreground">{d.id}</span>
                </span>
                <Badge variant="outline" className={`text-[10px] ${st.cls}`}>
                  {st.label}
                </Badge>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <div className="col-span-12 lg:col-span-8 space-y-6">
        {!driver ? null : !draft ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="p-10 text-center space-y-4">
              <i className="fas fa-id-card text-4xl text-muted-foreground"></i>
              <p className="font-semibold">{driver.name} has no rate card</p>
              <p className="text-sm text-muted-foreground">Manifests for this driver can't be posted until the card exists.</p>
              <div className="flex flex-wrap justify-center gap-2">
                <select id="driver-card-copy-from" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="bg-background border border-border rounded text-sm h-9 px-2">
                  <option value="">Start blank</option>
                  {others.map((c) => (
                    <option key={c.driverId} value={c.driverId}>
                      Copy from {c.driverName}
                    </option>
                  ))}
                </select>
                <Button id="driver-card-create" onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Create rate card
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <SaveBar idPrefix="driver-card" dirty={dirty} saving={saving} onSave={() => run(() => onSave(draft))} onDiscard={() => setDraft(existing || null)}>
              <span className="font-semibold">{driver.name}</span>
              <FileActions idPrefix="driver-card" kind="driver" card={draft} fileName={`driver_rate_card_${driver.id}.xlsx`} onImported={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
              {existing && (
                <Button id="driver-card-delete" size="sm" variant="ghost" className="text-red-500" onClick={() => run(() => onDelete(driver.id))}>
                  <i className="fas fa-trash mr-2"></i>Delete card
                </Button>
              )}
            </SaveBar>

            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Pay per piece — zones from the {customerCard.customer} card</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left p-3">Zone</th>
                      <th className="text-right p-3">Customer rate</th>
                      <th className="text-right p-3">Driver rate</th>
                      <th className="text-right p-3">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviced.map(({ zone, rate }) => {
                      const d = draft.zones?.[zone];
                      return (
                        <tr key={zone} className="border-t border-border">
                          <td className="p-3 font-semibold">Zone {zone}</td>
                          <td className="p-3 text-right text-muted-foreground">{rate == null ? 'N/A' : money(rate)}</td>
                          <td className="p-2">
                            <NumField
                              id={`driver-rate-${zone}`}
                              value={d}
                              allowBlank
                              onCommit={(v) => setDraft((x) => ({ ...x, zones: { ...x.zones, [zone]: v } }))}
                              className={`w-24 ml-auto ${rate != null && d == null ? 'border-amber-500/60' : ''}`}
                            />
                          </td>
                          <td className={`p-3 text-right ${rate != null && d != null && rate - d < 0 ? 'text-red-500' : ''}`}>{rate != null && d != null ? money(rate - d) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">Weight pay (highest matching tier applies)</CardTitle>
              </CardHeader>
              <CardContent>
                <TierEditor idPrefix="driver-weight" kind="weight" tiers={draft.weightTiers || []} onChange={(t) => setDraft((x) => ({ ...x, weightTiers: t }))} />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Taxes

const TaxEditor = ({ taxes, onSave }) => {
  const [draft, setDraft, dirty] = useDraft(taxes || []);
  const [saving, run] = useSaving();
  return (
    <Card className="bg-card border-border max-w-xl">
      <CardContent className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">Charged on customer invoices (manifest and manual). Driver pay never carries GST/QST.</p>
        {draft.map((t) => (
          <div key={t.code} className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">{t.code}</span>
            <NumField
              id={`rate-tax-${t.code}`}
              value={t.rate}
              step="0.00001"
              onCommit={(v) => setDraft((d) => d.map((x) => (x.code === t.code ? { ...x, rate: v, label: `${x.code} ${+(v * 100).toFixed(3)}%` } : x)))}
              className="w-28"
            />
          </div>
        ))}
        <SaveBar idPrefix="taxes" dirty={dirty} saving={saving} onSave={() => run(() => onSave(draft))} onDiscard={() => setDraft(taxes)} />
      </CardContent>
    </Card>
  );
};

/**
 * Screen 3 — Rate Card Matrix
 * Customer cards (from the customer: zones, FSA map, accessorials), one driver card per driver (zone pay + weight pay),
 * and invoice taxes. Saving re-prices unposted manifests only; posted manifests keep the pricing frozen at post time.
 */
const RateCardMatrix = ({ cards, manifests, drivers, onSaveCustomerCard, onSaveDriverCard, onDeleteDriverCard, onSaveTaxes }) => {
  const [tab, setTab] = useState('customer');
  const missingDrivers = drivers.filter((d) => !cards.drivers?.[d.id]).length;

  return (
    <div className="space-y-6" id="rate-card-screen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Rate Card Matrix</h2>
          <p className="text-sm text-muted-foreground">Billing and driver pay are per piece. Changes re-price unposted manifests only.</p>
        </div>
        <div className="flex gap-2">
          {[
            ['customer', 'Customer rate cards'],
            ['driver', `Driver rate cards${missingDrivers ? ` (${missingDrivers} missing)` : ''}`],
            ['taxes', 'Taxes'],
          ].map(([key, label]) => (
            <Button key={key} id={`rate-card-tab-${key}`} size="sm" variant={tab === key ? 'default' : 'outline'} onClick={() => setTab(key)}>
              {label}
            </Button>
          ))}
        </div>
      </div>
      {tab === 'customer' && <CustomerCardEditor cards={cards} manifests={manifests} onSave={onSaveCustomerCard} />}
      {tab === 'driver' && <DriverCardEditor cards={cards} drivers={drivers} onSave={onSaveDriverCard} onDelete={onDeleteDriverCard} />}
      {tab === 'taxes' && <TaxEditor taxes={cards.taxes} onSave={onSaveTaxes} />}
    </div>
  );
};

export default RateCardMatrix;
